import React, { useEffect, useState } from "react";
import { FormControl, Input, InputLabel, makeStyles } from "@material-ui/core";
import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import { MailOutline } from "@material-ui/icons";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import { masterApi } from "functions/apis";
import SindoDialog from "components/Dialog/SindoDialog";

const useStyles = makeStyles(styles);

const defaultValue = {
  code: "",
  name: "",
  embarkationPortID: "",
  destinationPortID: "",
  sectorID: ""
};

export function CreateRoute(props) {
  const classes = useStyles();

  const createRouteSignal = React.useRef(new AbortController());

  const [payload, setPayload] = useState(defaultValue);
  const [disabled, setDisabled] = useState(false);
  const [redirect, setRedirect] = useState(false);

  // sector state start
  const [sectorList, setSectorList] = useState([]);
  const [sectorPages, setSectorPages] = useState(0);
  const [sectorPagination, setSectorPagination] = React.useState({
    pageIndex: 1,
    pageSize: 5
  });
  const [sectorFilter, setSectorFilter] = useState({
    searchString: "",
    publicationStatus: 1
  });
  const [sectorLoading, setSectorLoading] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);

  const [showSectorModal, setShowSectorModal] = useState(false);
  // sector state end

  // embarkation port state start
  const [showEmbarkationPortModal, setShowEmbarkationPortModal] = useState(
    false
  );
  const [embarkationPortList, setEmbarkationPortList] = useState([]);
  const [embarkationPortPages, setEmbarkationPortPages] = useState(0);
  const [embarkationPortPagination, setEmbarkationPortPagination] = useState({
    pageIndex: 1,
    pageSize: 5
  });
  const [embarkationPortFilter, setEmbarkationPortFilter] = useState({
    searchString: "",
    publicationStatus: 1
  });
  const [embarkationPortLoading, setEmbarkationPortLoading] = useState(false);
  const [embarkationSelectedPort, setEmbarkationSelectedPort] = useState(null);

  // embarkation port state end

  // destination port state start
  const [showDestinationPortModal, setShowDestinationPortModal] = useState(
    false
  );
  const [destinationPortList, setDestinationPortList] = useState([]);
  const [destinationPortPages, setDestinationPortPages] = useState(0);
  const [destinationPortPagination, setDestinationPortPagination] = useState({
    pageIndex: 1,
    pageSize: 5
  });
  const [destinationPortFilter, setDestinationPortFilter] = useState({
    searchString: "",
    publicationStatus: 1
  });
  const [destinationPortLoading, setDestinationPortLoading] = useState(false);
  const [destinationSelectedPort, setDestinationSelectedPort] = useState(null);

  // embarkation port state end

  useEffect(() => {
    return () => createRouteSignal.current.abort();
  }, []);

  useEffect(() => {
    setSectorLoading(true);
    masterApi
      .get("Sectors", {
        signal: createRouteSignal.current.signal,
        params: {
          filter: sectorFilter,
          pagination: sectorPagination
        }
      })
      .then(data => {
        setSectorList(
          data.records.map(record => {
            return {
              ...record,
              actions: (
                <div className="actions-right">
                  <a
                    onClick={() => {
                      setPayload(prev => ({
                        ...prev,
                        sectorID: record.id
                      }));
                      setShowSectorModal(false);
                      setSelectedSector(record);
                    }}
                  >
                    <Button color="info" className="like">
                      Select
                    </Button>
                  </a>
                </div>
              )
            };
          })
        );
        setSectorPages(
          Math.ceil(data.totalRecords / sectorPagination.pageSize)
        );
        setSectorLoading(false);
      })
      .catch(error => console.log(error))
      .finally(() => setSectorLoading(false));
  }, [sectorFilter, sectorPagination]);

  useEffect(() => {
    setEmbarkationPortLoading(true);
    masterApi
      .get("Ports", {
        signal: createRouteSignal.current.signal,
        params: {
          filter: embarkationPortFilter,
          pagination: embarkationPortPagination
        }
      })
      .then(data => {
        setEmbarkationPortList(
          data.records.map(record => {
            return {
              ...record,
              actions: (
                <div className="actions-right">
                  <a
                    onClick={() => {
                      setPayload(prev => ({
                        ...prev,
                        embarkationPortID: record.id
                      }));
                      setShowEmbarkationPortModal(false);
                      setEmbarkationSelectedPort(record);
                    }}
                  >
                    <Button color="info" className="like">
                      Select
                    </Button>
                  </a>
                </div>
              )
            };
          })
        );
        setEmbarkationPortPages(
          Math.ceil(data.totalRecords / sectorPagination.pageSize)
        );
      })
      .catch(error => console.log(error))
      .finally(() => setEmbarkationPortLoading(false));
  }, [embarkationPortFilter, embarkationPortPagination]);

  useEffect(() => {
    setDestinationPortLoading(true);
    masterApi
      .get("Ports", {
        signal: createRouteSignal.current.signal,
        params: {
          filter: destinationPortFilter,
          pagination: destinationPortPagination
        }
      })
      .then(data => {
        setDestinationPortList(
          data.records.map(record => {
            return {
              ...record,
              actions: (
                <div className="actions-right">
                  <a
                    onClick={() => {
                      setPayload(prev => ({
                        ...prev,
                        destinationPortID: record.id
                      }));
                      setShowDestinationPortModal(false);
                      setDestinationSelectedPort(record);
                    }}
                  >
                    <Button color="info" className="like">
                      Select
                    </Button>
                  </a>
                </div>
              )
            };
          })
        );
        setDestinationPortPages(
          Math.ceil(data.totalRecords / sectorPagination.pageSize)
        );
      })
      .catch(error => console.log(error))
      .finally(() => setDestinationPortLoading(false));
  }, [destinationPortFilter, destinationPortPagination]);

  const handleCodeChanged = e =>
    setPayload({ ...payload, code: e.target.value ?? "" });

  const handleNameChanged = e =>
    setPayload({ ...payload, name: e.target.value ?? "" });

  const handleSearchStringChanged = e => {
    setSectorFilter({
      ...sectorFilter,
      searchString: e.target.value === "" ? null : e.target.value
    });
  };

  const handleEmbarkationPortSearchStringChanged = e => {
    setEmbarkationPortFilter({
      ...embarkationPortFilter,
      searchString: e.target.value === "" ? null : e.target.value
    });
  };

  const handleDestinationPortSearchStringChanged = e => {
    setDestinationPortFilter({
      ...destinationPortFilter,
      searchString: e.target.value === "" ? null : e.target.value
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);

    masterApi
      .post("Routes", payload, {
        signal: createRouteSignal.current.signal
      })
      .then(() => setRedirect(true))
      .catch(error => {
        console.log(error);
        setDisabled(false);
      });
  };

  return redirect ? (
    <Redirect to={"/admin/Route/InDraft"} />
  ) : (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>CREATE NEW ROUTE</h4>
          </CardHeader>
          <CardBody>
            <Button onClick={() => props.history.goBack()} value="Back">
              BACK
            </Button>
            <form onSubmit={handleSubmit}>
              <GridContainer>
                <GridItem xs={6}>
                  <CustomInput
                    labelText="Code"
                    formControlProps={{
                      fullWidth: true
                    }}
                    inputProps={{
                      onChange: handleCodeChanged,
                      value: payload.code ?? ""
                    }}
                  />
                </GridItem>
                <GridItem xs={6}>
                  <CustomInput
                    labelText="Name"
                    formControlProps={{
                      fullWidth: true
                    }}
                    inputProps={{
                      onChange: handleNameChanged,
                      value: payload.name ?? ""
                    }}
                  />
                </GridItem>
                <GridItem xs={12}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="selSectorID"
                      className={classes.selectLabel}
                    >
                      Sector
                    </InputLabel>
                    <Input
                      onClick={() => setShowSectorModal(prev => !prev)}
                      className={classes.select}
                      value={selectedSector ? selectedSector.name : ""}
                    />
                    <SindoDialog
                      openModal={showSectorModal}
                      setOpenModal={val => setShowSectorModal(val)}
                      title="Sector"
                      pages={sectorPages}
                      pagination={sectorPagination}
                      loading={sectorLoading}
                      data={sectorList}
                      columns={[
                        {
                          id: "code",
                          Header: "Code",
                          accessor: d => d.code
                        },
                        {
                          id: "name",
                          Header: "Name",
                          accessor: d => d.name
                        },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          sortable: false,
                          filterable: false
                        }
                      ]}
                      handleSearchStringChanged={handleSearchStringChanged}
                      searchString={sectorFilter.searchString}
                      setPagination={setSectorPagination}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={6}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="selEmbarkationPortID"
                      className={classes.selectLabel}
                    >
                      Embarkation Port
                    </InputLabel>
                    <Input
                      onClick={() => setShowEmbarkationPortModal(prev => !prev)}
                      className={classes.select}
                      value={
                        embarkationSelectedPort
                          ? embarkationSelectedPort.name
                          : ""
                      }
                    />
                    <SindoDialog
                      openModal={showEmbarkationPortModal}
                      setOpenModal={val => setShowEmbarkationPortModal(val)}
                      title="Embarkation Port"
                      pages={embarkationPortPages}
                      pagination={embarkationPortPagination}
                      loading={embarkationPortLoading}
                      data={embarkationPortList}
                      columns={[
                        {
                          id: "code",
                          Header: "Code",
                          accessor: d => d.code
                        },
                        {
                          id: "name",
                          Header: "Name",
                          accessor: d => d.name
                        },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          sortable: false,
                          filterable: false
                        }
                      ]}
                      handleSearchStringChanged={
                        handleEmbarkationPortSearchStringChanged
                      }
                      searchString={embarkationPortFilter.searchString}
                      setPagination={setEmbarkationPortPagination}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={6}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="selDestinationPortID"
                      className={classes.selectLabel}
                    >
                      Destination Port
                    </InputLabel>
                    <Input
                      onClick={() => setShowDestinationPortModal(prev => !prev)}
                      className={classes.select}
                      value={
                        destinationSelectedPort
                          ? destinationSelectedPort.name
                          : ""
                      }
                    />
                    <SindoDialog
                      openModal={showDestinationPortModal}
                      setOpenModal={val => setShowDestinationPortModal(val)}
                      title="Destination Port"
                      pages={destinationPortPages}
                      pagination={destinationPortPagination}
                      loading={destinationPortLoading}
                      data={destinationPortList}
                      columns={[
                        {
                          id: "code",
                          Header: "Code",
                          accessor: d => d.code
                        },
                        {
                          id: "name",
                          Header: "Name",
                          accessor: d => d.name
                        },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          sortable: false,
                          filterable: false
                        }
                      ]}
                      handleSearchStringChanged={
                        handleDestinationPortSearchStringChanged
                      }
                      searchString={destinationPortFilter.searchString}
                      setPagination={setDestinationPortPagination}
                    />
                  </FormControl>
                </GridItem>
                <GridItem xs={12} md={12}>
                  <Button
                    disabled={disabled}
                    color="rose"
                    type="submit"
                    value="Submit"
                  >
                    Submit
                  </Button>
                </GridItem>
              </GridContainer>
            </form>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

CreateRoute.propTypes = {
  history: PropTypes.object
};
